// RD-UI-039 (KAN-666) — ONE shared, memory-only store for Account → Security →
// Sessions. The Sessions line, the expanded panel, the dashboard prefetch and
// the pull-to-refresh all read this single copy, so a count and its list can
// never disagree. Pattern: mfaStatusPrefetch.js (in-flight dedupe, epoch guard).
// Per-user memory (device names, masked IP, location): cleared on BOTH sign-out
// paths (clearPerUserDeviceState / clearSignedOutMemoryState).
import { useSyncExternalStore } from 'react';
import { getCurrentAuthAal, listAccountSessions } from './accountSessions';

export const SESSIONS_FRESH_MS = 30000;

const EMPTY = Object.freeze({
  userId: null, status: 'idle', sessions: Object.freeze([]), emptyReason: null, fetchedAt: 0,
});

let state = EMPTY;
let epoch = 0; // bumped by clear: a request started before it writes nothing
// Bumped by every patch (a revoke the server confirmed): a read that was already
// in flight saw the list BEFORE that write, so its answer is dropped — otherwise
// it would put a just-revoked device back on the list (code review, KAN-666).
let patchSeq = 0;
const inflight = new Map();
const listeners = new Set();

function notify() {
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

function setState(next) {
  state = next;
  notify();
}

/** State for this user only — never another user's list. */
export function peekAccountSessions(userId) {
  return userId && state.userId === userId ? state : null;
}

export function subscribeAccountSessions(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** @returns {Promise<object|null>} never rejects. */
export function loadAccountSessions(userId, { force = false } = {}) {
  if (!userId) return Promise.resolve(null);
  if (inflight.has(userId)) return inflight.get(userId);
  const cur = peekAccountSessions(userId);
  if (!force && cur && cur.status === 'ready' && Date.now() - cur.fetchedAt < SESSIONS_FRESH_MS) {
    return Promise.resolve(cur);
  }
  const startEpoch = epoch;
  const startPatch = patchSeq;
  if (!cur) {
    setState({ ...EMPTY, userId, status: 'loading' });
  } else if (cur.status !== 'ready') {
    setState({ ...cur, status: 'loading' });
  }
  const p = (async () => {
    try {
      const { sessions } = await listAccountSessions();
      let emptyReason = null;
      if (sessions.length === 0) {
        const aal = await getCurrentAuthAal();
        emptyReason = aal === 'aal2' ? 'session_ended' : 'mfa';
      }
      if (startEpoch !== epoch) return null; // signed out meanwhile — write nothing
      if (startPatch !== patchSeq) return peekAccountSessions(userId); // a revoke landed mid-read — keep it
      setState({
        userId, status: 'ready', sessions, emptyReason, fetchedAt: Date.now(),
      });
    } catch (err) {
      if (startEpoch !== epoch) return null;
      const had = peekAccountSessions(userId);
      if (had && had.fetchedAt > 0) {
        // A failed background reconcile is not an action failure — keep the list.
        console.warn('listAccountSessions (kept existing list)', err);
        if (had.status !== 'ready') setState({ ...had, status: 'ready' });
      } else {
        console.error('listAccountSessions', err);
        setState({ ...EMPTY, userId, status: 'error' });
      }
    } finally {
      if (inflight.get(userId) === p) inflight.delete(userId);
    }
    return peekAccountSessions(userId);
  })();
  inflight.set(userId, p);
  return p;
}

/** Optimistic revoke result — the write is the source of truth. */
export function patchAccountSessions(userId, nextSessions) {
  const cur = peekAccountSessions(userId);
  if (!cur) return;
  patchSeq += 1;
  setState({
    ...cur,
    status: 'ready',
    sessions: nextSessions,
    emptyReason: nextSessions.length === 0 ? cur.emptyReason : null,
    fetchedAt: Date.now(),
  });
}

export function clearAccountSessionsStore() {
  epoch += 1;
  inflight.clear();
  state = EMPTY;
  notify();
}

/** User-scoped snapshot with a stable identity between changes. */
export function useAccountSessionsStore(userId) {
  return useSyncExternalStore(
    subscribeAccountSessions,
    () => (userId && state.userId === userId ? state : EMPTY),
    () => EMPTY,
  );
}
